import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuthStore } from "./store/authStore";
import { Avatar, Typography, TextField, Button, Box, Alert, Chip, Stack, FormControlLabel, Switch, IconButton } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard from "./components/DataCard";
import Footer from "./Footer";
import apiClient from "./utils/apiClient";
import PasswordField from "./PasswordField";

const AVATAR_MAX_DIM = 160; // px — small enough to keep the resulting data URI a few KB
const AVATAR_JPEG_QUALITY = 0.85;

// Reads an image File, center-crops it to a square, downsizes it, and
// returns a compact JPEG data URI — small enough to store directly on the
// user row (avatar_url is a plain TEXT column) without any new file-storage
// backend. Rejects non-images and anything that fails to decode.
function downscaleImageToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file.type || !file.type.startsWith("image/")) {
      reject(new Error("Please choose an image file."));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode the selected file as an image."));
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = AVATAR_MAX_DIM;
        canvas.height = AVATAR_MAX_DIM;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_MAX_DIM, AVATAR_MAX_DIM);
        resolve(canvas.toDataURL("image/jpeg", AVATAR_JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const Profile = () => {
  const { id } = useParams(); // Get user ID from URL
  const user = useAuthStore((state) => state.user);
  const [displayUser, setDisplayUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    fullname: "",
    password: "",
    confirmPassword: "",
  });
  const [avatarPreview, setAvatarPreview] = useState(null); // data URI, only set once the user picks a new image this edit session
  const [avatarError, setAvatarError] = useState(null);
  const avatarInputRef = useRef(null);

  const [message, setMessage] = useState({ text: "", type: "" });
  const [notificationsSaving, setNotificationsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      apiClient.get(`/user/${id}`).then(setDisplayUser).catch(() => setDisplayUser(null));
    } else {
      setDisplayUser(user);
    }
  }, [id, user]);

  // Only the signed-in user's own preference — viewing someone else's
  // profile (InternalUser/Admin) never shows or edits their notification setting.
  const isOwnProfile = !id || id === user?.id;

  // report_notification_emails is a plain user property (User model), not a
  // separate opt-in row — undefined/null (not yet set) means enabled, same
  // convention the backend uses.
  const reportNotificationsEnabled = displayUser?.report_notification_emails !== false;

  const handleToggleReportNotifications = async (event) => {
    const enabled = event.target.checked;
    setNotificationsSaving(true);
    try {
      // fullname is included deliberately: update_user() still overwrites it
      // unconditionally from whatever's in the request body, so omitting it
      // here would blank out the user's name as a side effect of flipping
      // this one preference.
      const updated = await apiClient.put(`/user/${displayUser.id}`, {
        id: displayUser.id,
        email: displayUser.email,
        fullname: displayUser.fullname,
        report_notification_emails: enabled,
      });
      setDisplayUser((prev) => ({ ...prev, ...updated }));
    } catch (error) {
      console.error("Error updating notification preference:", error);
      setMessage({ text: "Failed to update notification preference", type: "error" });
    } finally {
      setNotificationsSaving(false);
    }
  };

  // Determine which user to display
  // const displayUser = id ? null : user; // Assuming getUserById is removed

  if (!displayUser) {
    return <Typography>Loading...</Typography>;
  }

  const isSsoUser = displayUser.auth_provider === "oauth";

  const formatEpochTimestamp = (epoch) => {
    if (!epoch) return "N/A";
    const date = new Date(epoch);
    return date.toLocaleString();
  };

  const handleToggleEdit = () => {
    if (!isEditing) {
      // Initialize form with current values when starting to edit
      setPasswordForm({
        fullname: displayUser.fullname || "",
        password: "",
        confirmPassword: "",
      });
      setAvatarPreview(null);
      setAvatarError(null);
    }
    setIsEditing(!isEditing);
    setMessage({ text: "", type: "" }); // Clear any previous messages
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setAvatarError(null);
    try {
      const dataUrl = await downscaleImageToDataUrl(file);
      setAvatarPreview(dataUrl);
    } catch (error) {
      console.error("Error processing avatar image:", error);
      setAvatarError(error.message || "Could not use that image.");
    }
  };

  const handleInputChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setMessage({ text: "Passwords do not match", type: "error" });
      return;
    }

    try {
      const userToSubmit = {
        id: displayUser.id,
        email: displayUser.email,
        fullname: passwordForm.fullname,
        roles: displayUser.roles, // Include roles in the submission
      };

      // Only sent when the user actually picked a new image this edit
      // session — omitting it otherwise leaves the existing avatar_url
      // untouched (see update_user's handling).
      if (avatarPreview) {
        userToSubmit.avatar_url = avatarPreview;
      }

      // Sent as plaintext (over HTTPS) rather than client-hashed — the
      // backend hashes it server-side with the real scrypt KDF. Sending a
      // bare client-computed SHA-256 digest let *that* digest become the
      // actual stored credential (unsalted, GPU-crackable). Same field the
      // Admin Users page and /v1/register already use.
      if (passwordForm.password && !isSsoUser) {
        userToSubmit.password = passwordForm.password;
      }

      const updated = await apiClient.put(`/user/${displayUser.id}`, userToSubmit);
      setDisplayUser((prev) => ({ ...prev, ...updated }));
      setAvatarPreview(null);
      setMessage({ text: "Profile updated successfully", type: "success" });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({ text: "Failed to update profile", type: "error" });
    }
  };

  return (
    <PageShell>
      <PageContainer maxWidth={720}>
        <PageHeader
          icon={<PersonIcon />}
          title="Profile"
          subtitle="Your account details and security"
        />
        <DataCard>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Box sx={{ position: "relative", width: 96, height: 96 }}>
              <Avatar
                sx={{ width: 96, height: 96 }}
                src={avatarPreview || displayUser.avatar_url}
                alt={displayUser.fullname}
              />
              {isEditing && (
                <>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleAvatarFileChange}
                  />
                  <IconButton
                    size="small"
                    onClick={() => avatarInputRef.current?.click()}
                    aria-label="Change avatar"
                    sx={{
                      position: "absolute",
                      bottom: -4,
                      right: -4,
                      backgroundColor: "primary.main",
                      color: "#fff",
                      "&:hover": { backgroundColor: "primary.dark" },
                    }}
                  >
                    <PhotoCameraIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>

            {avatarError && (
              <Alert severity="error" sx={{ width: "100%" }} onClose={() => setAvatarError(null)}>
                {avatarError}
              </Alert>
            )}

            {message.text && (
              <Alert severity={message.type} sx={{ width: "100%" }}>
                {message.text}
              </Alert>
            )}

            {isEditing ? (
              <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%", maxWidth: 420 }}>
                <TextField
                  label="Email"
                  value={displayUser.email}
                  fullWidth
                  margin="normal"
                  disabled
                />
                <TextField
                  label="Full Name"
                  value={passwordForm.fullname}
                  onChange={(e) => handleInputChange("fullname", e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                {isSsoUser ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This account signs in through SSO, so the password is managed by your
                    identity provider and can't be changed here.
                  </Alert>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                      Leave the password fields blank if you don't want to change your password.
                    </Typography>
                    <PasswordField
                      value={passwordForm.password}
                      onChange={(value) => handleInputChange("password", value)}
                      confirmValue={passwordForm.confirmPassword}
                      confirmOnChange={(value) => handleInputChange("confirmPassword", value)}
                    />
                  </>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                  <Button variant="outlined" onClick={handleToggleEdit}>
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit" color="primary">
                    Save Changes
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Typography variant="h5" fontWeight={700}>
                  {displayUser.fullname}
                </Typography>
                <Box sx={{ color: "text.secondary", textAlign: "center", lineHeight: 1.9 }}>
                  <div>
                    Email{" "}
                    <a
                      href={`mailto:${displayUser.email}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      {displayUser.email}
                    </a>
                  </div>
                  <div>Logins {displayUser.login_count}</div>
                  <div>Last Login {formatEpochTimestamp(displayUser.last_login)}</div>
                  <div>
                    Last Unsuccessful {formatEpochTimestamp(displayUser.last_unsuccessful_login)}
                  </div>
                </Box>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  {(displayUser.roles || []).length > 0 ? (
                    displayUser.roles.map((role) => <Chip key={role} label={role} size="small" />)
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No roles assigned
                    </Typography>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleToggleEdit}
                  sx={{ mt: 1 }}
                >
                  Edit Profile
                </Button>
              </>
            )}
          </Box>
        </DataCard>

        {isOwnProfile && (
          <DataCard title="Notifications">
            <FormControlLabel
              control={
                <Switch
                  checked={reportNotificationsEnabled}
                  onChange={handleToggleReportNotifications}
                  disabled={notificationsSaving}
                />
              }
              label="Email me when a report finishes"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sent when an analysis you started completes or errors out, with a link straight to the report.
            </Typography>
          </DataCard>
        )}
      </PageContainer>
    </PageShell>
  );
};

export default Profile;
